import React, { FC, useState } from 'react';
import styles from './styles.module.css';
import cx from 'classnames';
import { Tooltip, Button } from 'antd';
import { ExternalLink } from 'lucide-react';
import { Role } from '../../../../models/User';
import DetailsLabel from '../DetailsLabel';
import { LinkedTask } from '../../../../../../redesign/javascript/views/task_details/useLinkedTasks';
import { mentionReplacedDescription } from '../../../../../../javascript/components/HighlightedMentions/utils';

type LinkedTasksProps = {
  linkedTasks: LinkedTask[];
  viewLinkedTasks: boolean;
  loading: boolean;
  error: Error | null;
  container: HTMLDivElement;
  projectId: number;
  bugherdUrl: string;
  isAdminView: boolean;
  currentUserRole: Role;
  onLinkedTaskSelect?: (taskId: number) => void;
};

const LinkedTasks: FC<LinkedTasksProps> = ({
  linkedTasks,
  loading,
  error,
  viewLinkedTasks,
  container,
  projectId,
  bugherdUrl,
  isAdminView,
  currentUserRole,
  onLinkedTaskSelect
}) => {
  const [tooltipOpen, setTooltipOpen] = useState<false | number>(false);
  const notGuest = currentUserRole !== Role.GUEST;
  const viewableLinkedTasks = linkedTasks.filter(({ canView }) => canView);
  if (loading || error || !viewLinkedTasks || !viewableLinkedTasks.length) {
    return null;
  }

  const handleViewLinkedTask = (
    taskId: number,
    localTaskId: number,
    open: boolean
  ) => {
    if (onLinkedTaskSelect) {
      onLinkedTaskSelect(taskId);
      return;
    }
    if (open || notGuest) {
      window.open(
        `${bugherdUrl}/projects/${projectId}/tasks/${localTaskId}`,
        isAdminView ? '_self' : '_blank'
      );
    }
  };

  return (
    <div className={styles.linkedTasksWrapper}>
      <DetailsLabel label="LINKED TASKS:" />
      <div className={styles.linkedTasksList}>
        {viewableLinkedTasks.map(
          ({ id, localTaskId, title, description, priority, open }, index) => (
            <Tooltip
              title={
                <span className={styles.linkedTaskPreview}>
                  {title && (
                    <span className={styles.linkedTaskPreviewTitle}>
                      {title}
                    </span>
                  )}
                  {description && (
                    <span className={styles.linkedTaskPreviewDescription}>
                      {mentionReplacedDescription(description)}
                    </span>
                  )}
                </span>
              }
              getPopupContainer={() => container}
              key={id}
              overlayClassName={styles.linkedTaskTooltip}
              placement="bottomRight"
              trigger={['hover']}
              open={tooltipOpen === id}
              onOpenChange={open => setTooltipOpen(open ? id : false)}
            >
              <div
                className={cx(styles.linkedTaskItem, {
                  [styles.linkedTaskItemHover]: currentUserRole !== Role.GUEST,
                  [styles.linkedTaskItemLast]:
                    index === viewableLinkedTasks.length - 1
                })}
                key={id}
                onClick={() => handleViewLinkedTask(id, localTaskId, open)}
              >
                <div
                  className={cx(
                    styles.linkedTaskLocalTaskId,
                    styles[`priority-${priority}`]
                  )}
                >
                  {localTaskId}
                </div>
                <span className={styles.linkedTaskTitleOrDescription}>
                  {title || description}
                </span>
                {notGuest && (
                  <Button
                    type="link"
                    className={styles.linkedTaskExternalLink}
                    href={`${bugherdUrl}/projects/${projectId}/tasks/${localTaskId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    icon={<ExternalLink size={18} />}
                    onClick={event => event.stopPropagation()}
                  />
                )}
              </div>
            </Tooltip>
          )
        )}
      </div>
    </div>
  );
};

export default LinkedTasks;
